import {Suspense} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import cn from 'clsx';
import {AUTH_INIT_MODELS, SHORT_LINK_TYPE} from '@chekin/core';
import {DEVICE_BREAKPOINTS, useScreenResize} from '@chekinapp/ui';
import {FIND_BOOKING_ROUTES, PATHS} from 'routes/constants';
import {useChekinSDK} from '../../context/ChekinSDK';
import {useLayoutMode} from 'hooks/useLayoutMode';
import PageSideBar from '../PageSideBar';
import {ViewLoader} from 'pages/Auth/components/ViewLoader';
import {useKioskoStore} from 'context/kiosko';
import {LayoutWrapper} from 'components/common/LayoutWrapper';
import {useVela, VelaPanel} from 'vela';
import {useCollapsibleRail} from 'hooks/useCollapsibleRail';
import {RAIL_GRID_TRANSITION, VELA_RAIL_GRID} from 'components/CollapsibleRail';
import {PageContentSkeleton, SideBarSkeleton} from './RootLayoutSkeleton';

const LinksWithoutSidebar: string[] = [SHORT_LINK_TYPE.lock];

type Props = {
  isLoading?: boolean;
};
function RootLayout({isLoading}: Props) {
  const {linkType, isAuthInitialization, modelName} = useChekinSDK();
  const layoutMode = useLayoutMode();

  const {isMatch: isMobile} = useScreenResize(DEVICE_BREAKPOINTS.laptop);
  const [, , , token] = window.location.href.split('/');
  const isWrongPage = !token;
  const isLinkWithoutSidebar = LinksWithoutSidebar.includes(linkType ?? '');
  const isUniversalLink =
    modelName === AUTH_INIT_MODELS.housing || modelName === AUTH_INIT_MODELS.user;
  const isHousingGuidebooksLink = linkType === SHORT_LINK_TYPE.housingGuidebook;

  const location = useLocation();
  const isAuthFlowPage = location.pathname.includes(PATHS.auth.root);
  const isThanksPage = location.pathname.includes(PATHS.checkout.thanks);
  const isFindBookingPage = FIND_BOOKING_ROUTES.some(path =>
    location.pathname.includes(path),
  );

  const isKiosko = useKioskoStore(state => state.connection.isConnected);
  const isSideBarVisible =
    !isAuthInitialization &&
    !isMobile &&
    !isWrongPage &&
    !isLinkWithoutSidebar &&
    !isUniversalLink &&
    !isAuthFlowPage &&
    !isThanksPage &&
    !isFindBookingPage &&
    layoutMode === 'default';

  const invalidLink = !isSideBarVisible && !isUniversalLink;

  const vela = useVela();
  const velaRail = useCollapsibleRail('vela');
  const isVelaVisible =
    isSideBarVisible && !isLoading && !isUniversalLink && Boolean(vela) && !vela?.isEmpty;

  if (isLoading && layoutMode === 'kiosko') {
    return <ViewLoader />;
  }

  return (
    <LayoutWrapper isKiosko={isKiosko}>
      <div
        className={cn(
          'page-wrapper',
          'block',
          'lg:grid',

          (invalidLink || isFindBookingPage || isUniversalLink) && 'lg:grid-cols-[none]',
          !invalidLink &&
            !isFindBookingPage &&
            !isUniversalLink &&
            !isVelaVisible &&
            'lg:grid-cols-[266px_auto]',
          isVelaVisible && RAIL_GRID_TRANSITION,
          isVelaVisible &&
            (velaRail.isOpen ? VELA_RAIL_GRID.open : VELA_RAIL_GRID.collapsed),
          (invalidLink || isFindBookingPage || isUniversalLink) &&
            '[1126px]:grid-cols-[none]',
          invalidLink && '[1126px]:h-[var(--app-vh,100vh)]',
          isHousingGuidebooksLink && 'lg:grid-cols-[none]',
          'kiosko:md:block',
          'kiosko:[1126px]:block',
          !isHousingGuidebooksLink &&
            '[&_[data-slot=scroll-area-viewport]]:h-[var(--app-dvh,100dvh)]',
        )}
      >
        {isSideBarVisible && (isLoading ? <SideBarSkeleton /> : <PageSideBar />)}
        {isLoading ? (
          <PageContentSkeleton />
        ) : (
          <Suspense fallback={<PageContentSkeleton />}>
            <Outlet />
          </Suspense>
        )}
        {isVelaVisible && vela && <VelaPanel vela={vela} rail={velaRail} />}
      </div>
    </LayoutWrapper>
  );
}

export {RootLayout};
